import React from 'react';
import { ArrowUpRightIcon, CheckIcon, TargetIcon } from 'lucide-react';
import { CommissionStep, Role, ScorecardResult } from '../types/commission';
import { formatCurrency, nextStepForUnits, stepPayoutLabel, stepShortLabel } from '../utils/commission';
interface UnitBonusCardProps {
  role: Role;
  units: number;
  volume: number;
  scorecard: ScorecardResult;
  payingStep: CommissionStep;
  scorecardMet: boolean;
  isCurrent: boolean;
}
export function UnitBonusCard({
  role,
  units,
  volume,
  scorecard,
  payingStep,
  scorecardMet,
  isCurrent
}: UnitBonusCardProps) {
  const isBps = role.model === 'bps';
  const next = nextStepForUnits(role, units);
  const topStep = role.steps[role.steps.length - 1];
  const gated = role.requiresScorecard && !scorecardMet;
  const percent = Math.min(100, Math.round(units / Math.max(1, topStep.minUnits) * 100));
  const headline = gated ? 'Bonus not earned' : isBps ? payingStep.bonusBps ? `+${payingStep.bonusBps} BPS earned` : 'Base rate only' : payingStep.perUnit ? `${formatCurrency(payingStep.perUnit)} per ${role.incentiveNoun ?? 'unit'}` : 'No incentive yet';
  const nextValue = next ? isBps ? volume * ((next.totalBps ?? 0) - (payingStep.totalBps ?? 0)) / 10000 : units * ((next.perUnit ?? 0) - (payingStep.perUnit ?? 0)) : 0;
  const message = gated ? 'Scorecard goal missed — the plan pays the base rate this period.' : next && isCurrent ? `${next.minUnits - units} more ${role.unitNounPlural} unlocks ${stepPayoutLabel(role, next)}, worth about ${formatCurrency(nextValue)} more.` : next ? `Closed ${next.minUnits - units} ${role.unitNounPlural} short of the ${stepPayoutLabel(role, next)} band.` : `Top band reached — paying ${stepPayoutLabel(role, payingStep)}.`;
  return <article aria-labelledby="bonus-heading" className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-card" data-id="element-530" data-mp="1cim5je7q5pp3">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand-700" data-id="element-531" data-mp="37l932umnog0a">
        {isBps ? 'Unit bonus' : 'Unit incentive'}
      </p>
      <h2 id="bonus-heading" className="mt-1.5 text-2xl font-bold text-slate-900" data-id="element-532" data-mp="3ckveqzmu5o6z">
        {headline}
      </h2>

      <div role="progressbar" aria-label="Funded units toward the top unit bonus step" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} className="mt-6 h-2 overflow-hidden rounded-full bg-slate-100" data-id="element-533" data-mp="3mgyi28tfbsvi">
        <div className="h-full rounded-full bg-brand-600" style={{
        width: `${percent}%`
      }} data-id="element-534" data-mp="3vfto9kwgx2yt" />
      </div>

      <ol className="mt-4 grid gap-3" style={{
      gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))'
    }} data-id="element-535" data-mp="45bo88djujvm">
        {role.steps.map(step => {
        const reached = !gated && units >= step.minUnits;
        const isActive = !gated && step.minUnits === payingStep.minUnits;
        const showBpsSuffix = isBps && Boolean(step.bonusBps);
        const displayValue = isBps ? step.bonusBps ? `+${step.bonusBps}` : 'Base' : step.perUnit ? formatCurrency(step.perUnit) : 'None';
        return <li key={step.minUnits} className={`rounded-lg border px-3 py-2.5 ${isActive ? 'border-brand-600 bg-brand-50' : 'border-slate-200 bg-white'}`} data-id="element-536" data-mp="1tmgq72x0hyhf">
              <div className="flex items-center justify-between gap-2" data-id="element-537" data-mp="2ha6zx4r9as2g">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-538" data-mp="2hc1lmlacqo7l">{stepShortLabel(step)}</span>
                {reached ? <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-label="Reached" data-id="element-539" data-mp="27kx71mol95da" /> : null}
              </div>
              <p className={`mt-1 text-lg font-bold tabular-nums ${reached ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-540" data-mp="351m6j9q2fvji">
                {displayValue}
                {showBpsSuffix ? <span className="ml-1 text-xs font-semibold" data-id="element-541" data-mp="2kpqhaqn60ave">BPS</span> : null}
              </p>
            </li>;
      })}
      </ol>

      <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-slate-50 px-4 py-3" data-id="element-542" data-mp="2oq1y1scepr1l">
        <TargetIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-543" data-mp="2auq43n62s4lq" />
        <p className="text-sm text-slate-700" data-id="element-544" data-mp="2frym9nr848mz">{message}</p>
      </div>

      {role.requiresScorecard ? <div className="mt-auto pt-4" data-id="element-545" data-mp="33jdtnuxuxg53">
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm" data-id="element-546" data-mp="38gvroqac6ayb">
            <div className="flex items-center gap-3" data-id="element-547" data-mp="2tqg81fxkjb29">
              <span className="text-slate-500" data-id="element-548" data-mp="2tpn56q1qd5pe">Scorecard points</span>
              <a href="#/scorecard" onClick={event => event.preventDefault()} className="inline-flex items-center gap-1 font-semibold text-brand-700 transition hover:text-brand-900" data-id="element-549" data-mp="2yoq2tpfds9nc">
                View scorecard
                <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-550" data-mp="1fqlgqfrsgjkd" />
              </a>
            </div>
            <span className={`font-semibold tabular-nums ${scorecardMet ? 'text-slate-900' : 'text-red-600'}`} data-id="element-551" data-mp="1asa5c0lui101">
              {scorecard.points.toLocaleString()}
              <span className="font-medium text-slate-400" data-id="element-552" data-mp="1ki38qebu39t7"> / {scorecard.goal.toLocaleString()}</span>
            </span>
          </div>
        </div> : null}
    </article>;
}